import { useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'use-intl'
import { Card, Chip, Surface } from '@heroui/react'
import { ArrowLeft, Crown, Mountain, Award, MapPin, Utensils } from 'lucide-react'
import { LocalizedLink } from '~/components/common/localized-link'
import { categoryAggregateQueryOptions, parseCategoryAggregateId, rankingDetailQueryOptions, rankingShopsQueryOptions } from '../hooks/use-ranking-detail'
import { RestaurantCard } from '~/features/discovery/components/discovery/RestaurantCard'
import { mapShopToCardProps } from '~/features/search/lib/map-shop-to-card-props'

export function RankingDetailPage() {
  const { snapshotId } = useParams({ strict: false }) as { snapshotId?: string }
  const aggregate = snapshotId ? parseCategoryAggregateId(snapshotId) : null

  if (aggregate) {
    return <CategoryAggregateRankingPage aggregate={aggregate} />
  }

  return <SnapshotRankingDetailPage />
}

export function CategoryAggregateRankingPage({ aggregate }: { aggregate?: { year: number, categorySourceId: string } }) {
  const params = useParams({ strict: false }) as { year?: string, categorySourceId?: string }
  const { page = 1, region } = useSearch({ strict: false }) as { page?: number, region?: string }
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslations('rankings')
  const resolvedAggregate = aggregate || (
    params.year && params.categorySourceId
      ? { year: Number(params.year), categorySourceId: params.categorySourceId }
      : null
  )

  const { data } = useSuspenseQuery(categoryAggregateQueryOptions(resolvedAggregate, locale, region, page))

  if (!data) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16">
        <LocalizedLink to="/rankings" className="mb-8 inline-flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-stone-900 dark:hover:text-stone-100">
          <ArrowLeft className="h-4 w-4" />
          {t('backToRankings')}
        </LocalizedLink>
        <Surface className="p-8 text-center" variant="secondary">
          <Award className="mx-auto mb-4 h-10 w-10 text-stone-400" />
          <h1 className="text-2xl font-bold text-stone-900 dark:text-white">
            {t('notFoundTitle')}
          </h1>
          <p className="mt-3 text-sm text-stone-500 dark:text-stone-400">
            {t('notFoundDescription')}
          </p>
        </Surface>
      </div>
    )
  }

  const selectedRegion = data.regions.find((item: any) => item.sourceId === data.selectedRegion)
  const title = selectedRegion
    ? t('categoryRegionTitle', { year: data.year, region: selectedRegion.name, category: data.categoryName })
    : t('categoryAggregateTitle', { year: data.year, category: data.categoryName })
  const firstVisible = data.totalEntries > 0 ? ((data.pagination.currentPage - 1) * data.pagination.pageSize) + 1 : 0
  const lastVisible = data.totalEntries > 0 ? firstVisible + data.groups.reduce((sum: number, group: any) => sum + group.entries.length, 0) - 1 : 0

  const goToPage = (nextPage: number) => {
    navigate({
      to: data.href as any,
      search: {
        page: nextPage,
        region: data.selectedRegion,
      } as any,
    })
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <LocalizedLink to="/rankings" className="mb-6 inline-flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-stone-900 dark:hover:text-stone-100">
        <ArrowLeft className="h-4 w-4" />
        {t('backToRankings')}
      </LocalizedLink>

      <Surface className="mb-6 rounded-3xl p-6 md:p-8" variant="tertiary">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-primary">
              <Utensils className="size-5" />
              <span className="text-sm font-semibold">{t('meitenLabel')}</span>
            </div>
            <h1 className="text-3xl font-black tracking-tight md:text-4xl">
              {title}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-secondary dark:text-foreground/70">
              {selectedRegion
                ? t('categoryRegionDescription', { region: selectedRegion.name, category: data.categoryName })
                : t('categoryAggregateDescription', { category: data.categoryName })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end">
            <Chip variant="soft" color="accent">
              <Chip.Label>{t('entriesCount', { count: data.totalEntries })}</Chip.Label>
            </Chip>
            <Chip variant="soft">
              <Chip.Label>{t('regionCount', { count: data.totalRegions })}</Chip.Label>
            </Chip>
          </div>
        </div>
      </Surface>

      <section className="mb-8" aria-label={t('regionFilterLabel')}>
        <div className="mb-3 text-sm font-semibold text-stone-900 dark:text-stone-100">{t('regionFilterLabel')}</div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <LocalizedLink
            to={data.href}
            search={{ page: 1 }}
            className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Chip color={!data.selectedRegion ? 'accent' : 'default'} variant={!data.selectedRegion ? 'primary' : 'soft'} size="lg">
              <Chip.Label>{t('allRegions')}</Chip.Label>
            </Chip>
          </LocalizedLink>
          {data.regions.map((item: any) => (
            <LocalizedLink
              key={item.sourceId}
              to={data.href}
              search={{ region: item.sourceId, page: 1 }}
              className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Chip color={data.selectedRegion === item.sourceId ? 'accent' : 'default'} variant={data.selectedRegion === item.sourceId ? 'primary' : 'soft'} size="lg">
                <MapPin className="size-4" />
                <Chip.Label>{item.name}</Chip.Label>
              </Chip>
            </LocalizedLink>
          ))}
        </div>
      </section>

      <p className="mb-5 text-sm text-stone-500 dark:text-stone-400">
        {data.totalEntries > 0
          ? t('showingEntryRange', { first: firstVisible, last: lastVisible, total: data.totalEntries })
          : t('entriesCount', { count: 0 })}
      </p>

      <div className="space-y-10">
        {data.groups.map((group: any) => (
          <section key={group.snapshotId}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MapPin className="size-5 text-primary" />
                <h2 className="text-xl font-black">{group.name}</h2>
              </div>
              <Chip variant="soft">
                <Chip.Label>{t('entriesShort', { count: group.totalShops })}</Chip.Label>
              </Chip>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {group.entries.map((entry: any) => {
                const cardProps = mapShopToCardProps(entry.shop) as any
                cardProps.distance = ''
                return (
                  <Card key={`${group.snapshotId}-${entry.shop.id}`} className="p-0" variant="transparent">
                    <RestaurantCard
                      restaurant={cardProps}
                      onPress={() => navigate({ to: '/{-$locale}/shops/$shopId', params: { locale, shopId: String(entry.shop.id) } })}
                    />
                  </Card>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      {data.totalEntries === 0 && (
        <Surface className="py-20 text-center text-stone-500" variant="secondary">
          {t('noRestaurants')}
        </Surface>
      )}

      {data.pagination.totalPages > 1 && (
        <PaginationControls
          currentPage={data.pagination.currentPage}
          totalPages={data.pagination.totalPages}
          onPrevious={() => goToPage(data.pagination.currentPage - 1)}
          onNext={() => goToPage(data.pagination.currentPage + 1)}
        />
      )}
    </div>
  )
}

function SnapshotRankingDetailPage() {
  const { snapshotId = '' } = useParams({ strict: false }) as { snapshotId?: string }
  const { page = 1 } = useSearch({ strict: false }) as { page?: number }
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslations('rankings')

  const { data: snapshot } = useSuspenseQuery(rankingDetailQueryOptions(snapshotId, locale))
  const { data: shopsData } = useSuspenseQuery(rankingShopsQueryOptions(snapshotId, locale, page, Boolean(snapshot)))

  if (!snapshot) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16">
        <LocalizedLink to="/rankings" className="mb-8 inline-flex items-center gap-2 text-sm text-stone-500 transition-colors hover:text-stone-900 dark:hover:text-stone-100">
          <ArrowLeft className="h-4 w-4" />
          {t('backToRankings')}
        </LocalizedLink>
        <div className="rounded-lg border border-divider bg-content1 p-8 text-center">
          <Award className="mx-auto mb-4 h-10 w-10 text-stone-400" />
          <h1 className="text-2xl font-bold text-stone-900 dark:text-white">
            {t('notFoundTitle')}
          </h1>
          <p className="mt-3 text-sm text-stone-500 dark:text-stone-400">
            {t('notFoundDescription')}
          </p>
        </div>
      </div>
    )
  }

  const shops = (shopsData as any)?.data || []
  const pagination = (shopsData as any)?.pagination
  const totalRestaurants = pagination?.total ?? shops.length
  const currentPage = pagination?.currentPage ?? page
  const pageSize = pagination?.pageSize ?? shops.length
  const totalPages = pagination?.totalPages ?? 1
  const firstVisible = shops.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0
  const lastVisible = shops.length > 0 ? firstVisible + shops.length - 1 : 0

  // Determine badge style from snapshot metadata
  const awardLevel = (snapshot as any)?.awardLevel?.toLowerCase() || ''
  const isHyakumeiten = (snapshot as any)?.type === 'CATEGORY' || (snapshot as any)?.snapshotType === 'CATEGORY'

  const IconComponent = isHyakumeiten ? Mountain : (awardLevel ? Crown : Award)

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Back navigation */}
      <LocalizedLink to="/rankings" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        {t('backToRankings')}
      </LocalizedLink>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <IconComponent className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-stone-900 dark:text-white">
            {(snapshot as any)?.title || snapshotId}
          </h1>
        </div>
        {(snapshot as any)?.description && (
          <p className="text-stone-500 dark:text-stone-400 mt-2">
            {(snapshot as any).description}
          </p>
        )}
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
          {shops.length > 0
            ? t('showingRange', { first: firstVisible, last: lastVisible, total: totalRestaurants })
            : t('restaurantsCount', { count: totalRestaurants })}
        </p>
      </div>

      {/* Shop Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shops.map((shop: any) => {
          const cardProps = mapShopToCardProps(shop) as any
          cardProps.distance = ''
          return (
            <div
              key={shop.id}
              className="block"
            >
              <RestaurantCard
                restaurant={cardProps}
                onPress={() => navigate({ to: '/{-$locale}/shops/$shopId', params: { locale, shopId: String(shop.id) } })}
              />
            </div>
          )
        })}
      </div>

      {shops.length === 0 && (
        <div className="text-center py-20 text-stone-500">
          {t('noRestaurants')}
        </div>
      )}

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => navigate({ search: { page: currentPage - 1 } as any })}
          onNext={() => navigate({ search: { page: currentPage + 1 } as any })}
        />
      )}
    </div>
  )
}

function PaginationControls({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
}: {
  currentPage: number
  totalPages: number
  onPrevious: () => void
  onNext: () => void
}) {
  const t = useTranslations('rankings')

  return (
    <nav className="mt-8 flex flex-col gap-3 border-t border-divider pt-6 sm:flex-row sm:items-center sm:justify-between" aria-label="Ranking pages">
      <p className="text-sm text-stone-500 dark:text-stone-400">
        {t('pageOf', { page: currentPage, totalPages })}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={currentPage <= 1}
          className="min-h-11 rounded-md border border-divider px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-stone-200 dark:hover:bg-stone-800"
          onClick={onPrevious}
        >
          {t('previous')}
        </button>
        <button
          type="button"
          disabled={currentPage >= totalPages}
          className="min-h-11 rounded-md border border-divider px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-stone-200 dark:hover:bg-stone-800"
          onClick={onNext}
        >
          {t('next')}
        </button>
      </div>
    </nav>
  )
}
